import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { getAuthorizedPersons } from '../api/staffStudents';

interface AuthorizedPersonsDisplayProps {
  studentId: number;
}

export default function AuthorizedPersonsDisplay({ studentId }: AuthorizedPersonsDisplayProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['authorizedPersons', studentId],
    queryFn: () => getAuthorizedPersons(studentId),
    staleTime: 60000, // Cache for 1 minute
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load authorized persons. Please try again.
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const { student, family, authorized_persons } = data;

  // Separate persons with and without pickup rights
  const canPickup = authorized_persons.filter((p) => p.has_pickup_rights);
  const cannotPickup = authorized_persons.filter((p) => !p.has_pickup_rights);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Authorized Pickup Persons
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Family: {family.family_name}
        </Typography>

        {student.medical_notes && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Medical Notes:</strong> {student.medical_notes}
          </Alert>
        )}

        {canPickup.length === 0 ? (
          <Alert severity="warning">
            No authorized persons with pickup rights found.
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" color="success.main" sx={{ mt: 2, mb: 1 }}>
              ✓ Authorized for Pickup
            </Typography>
            <List dense>
              {canPickup.map((person) => (
                <ListItem
                  key={person.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {person.first_name} {person.last_name}
                        </Typography>
                        {person.id_check_required && (
                          <Chip
                            icon={<BadgeIcon />}
                            label="ID Required"
                            size="small"
                            color="warning"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {person.relationship} • {person.phone}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {cannotPickup.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Emergency Contacts Only (Cannot Pick Up)
            </Typography>
            <List dense>
              {cannotPickup.map((person) => (
                <ListItem
                  key={person.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    opacity: 0.6,
                  }}
                >
                  <ListItemIcon>
                    <BlockIcon color="disabled" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${person.first_name} ${person.last_name}`}
                    secondary={`${person.relationship} • ${person.phone}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
}
